import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Skeleton, Spinner } from '@pnl/ui';
import type {
  AccountMetricsResponse,
  ConsolidatedDashboard,
  DailySummaryResponse,
  EquityCurveResponse,
  TraderMeResponse,
} from '@pnl/shared';
import { api } from '../lib/api';
import { AccountHeader } from '../components/dashboard/AccountHeader';
import { Card } from '../components/dashboard/Card';
import { AccountInfoCard } from '../components/dashboard/AccountInfoCard';
import { AccountSelector, CONSOLIDATED } from '../components/dashboard/AccountSelector';
import { ConsolidatedView } from '../components/dashboard/ConsolidatedView';
import { CurrentResults } from '../components/dashboard/CurrentResults';
import { DailyLimits } from '../components/dashboard/DailyLimits';
import { DailySummary } from '../components/dashboard/DailySummary';
import { DisciplineScore } from '../components/dashboard/DisciplineScore';
import { EquityCurveChart } from '../components/dashboard/EquityCurveChart';
import { FundedObjectives } from '../components/dashboard/FundedObjectives';
import { OpenPositions } from '../components/dashboard/OpenPositions';
import { PendingDashboard } from '../components/dashboard/PendingDashboard';
import { ReplicatorCard } from '../components/dashboard/ReplicatorCard';
import { RestartOfferBanner } from '../components/dashboard/RestartOfferBanner';
import { PromoBanner } from '../components/dashboard/PromoBanner';
import { Statistics } from '../components/dashboard/Statistics';
import { TradingJournal } from '../components/dashboard/TradingJournal';
import { TradingObjectives } from '../components/dashboard/TradingObjectives';
import { ViolationBanner } from '../components/dashboard/ViolationBanner';

interface DashboardPageProps {
  /**
   * Mercado deste dashboard. O trader pertence a UM mercado (Trader.marketId),
   * então as contas dele são todas do mesmo — se o mercado pedido não é o dele,
   * o dashboard aparece completo no estado "sem conta", com o próximo passo.
   *
   * É assim que o Dashboard Forex existe hoje, antes da API da Nelogica: mesma
   * tela, mesmos cards, mesmo comportamento — só sem fonte de dados própria.
   * Quando a API chegar, os dados entram por aqui sem refazer a tela.
   */
  market?: 'B3' | 'FOREX';
}

export function DashboardPage({ market }: DashboardPageProps = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState<string>('');

  const meQuery = useQuery<TraderMeResponse>({
    queryKey: ['trader-me'],
    queryFn: () => api.getTraderMe(),
  });

  const traderMarket = meQuery.data?.trader?.marketCode;
  const marketMatches = !market || !traderMarket || traderMarket === market;
  const accounts = marketMatches ? (meQuery.data?.accounts ?? []) : [];
  // ?account= vem dos links de "Minhas contas". Só vale se a conta existir.
  const paramAccount = searchParams.get('account') ?? '';
  const validParam =
    paramAccount === CONSOLIDATED || accounts.some((a) => a.id === paramAccount)
      ? paramAccount
      : '';
  // Conta padrão do trader (setada na ativação / escolhida em "Minhas contas").
  const defaultId = meQuery.data?.trader?.defaultAccountId ?? '';
  const validDefault = accounts.some((a) => a.id === defaultId) ? defaultId : '';
  // Conta efetiva: escolha manual > ?account= > conta padrão > primeira.
  const effective = selected || validParam || validDefault || accounts[0]?.id || '';
  const isConsolidated = effective === CONSOLIDATED;
  const activeAccountId = isConsolidated ? undefined : effective;

  // Troca de conta no seletor: atualiza o state e o ?account= (compartilhável).
  function handleSelect(id: string) {
    setSelected(id);
    setSearchParams(id && id !== CONSOLIDATED ? { account: id } : {}, { replace: true });
  }

  // Atualização automática: sem WebSocket, usamos polling "inteligente" — a cada
  // 45s e ao voltar o foco pra aba — pra refletir novos CSVs sem F5.
  const LIVE_REFRESH = { refetchInterval: 45_000, refetchOnWindowFocus: true } as const;

  const metricsQuery = useQuery<AccountMetricsResponse>({
    queryKey: ['account-metrics', activeAccountId],
    queryFn: () => api.getAccountMetrics(activeAccountId!),
    enabled: Boolean(activeAccountId),
    ...LIVE_REFRESH,
  });

  const equityQuery = useQuery<EquityCurveResponse>({
    queryKey: ['equity-curve', activeAccountId],
    queryFn: () => api.getEquityCurve(activeAccountId!),
    enabled: Boolean(activeAccountId),
    ...LIVE_REFRESH,
  });

  const summaryQuery = useQuery<DailySummaryResponse>({
    queryKey: ['daily-summary', activeAccountId],
    queryFn: () => api.getDailySummary(activeAccountId!, 10),
    enabled: Boolean(activeAccountId),
    ...LIVE_REFRESH,
  });

  // Compras pagas aguardando ativação — alimentam o banner do dashboard de quem
  // ainda não tem conta. Só busca quando não há conta ativa.
  const pendingQuery = useQuery({
    queryKey: ['trader-pending-activations'],
    queryFn: () => api.getTraderPendingActivations(),
    enabled: !meQuery.isLoading && accounts.length === 0,
  });

  const consolidatedQuery = useQuery<ConsolidatedDashboard>({
    queryKey: ['consolidated'],
    queryFn: () => api.getConsolidatedDashboard(),
    enabled: isConsolidated,
    ...LIVE_REFRESH,
  });

  if (meQuery.isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Spinner size={24} />
      </div>
    );
  }

  // Sem conta ativa o dashboard NÃO fica vazio: mostra a estrutura completa com
  // o próximo passo no topo (pedido do cliente). Nenhum número é inventado —
  // ver PendingDashboard.
  if (accounts.length === 0) {
    return <PendingDashboard pending={pendingQuery.data?.items ?? []} />;
  }

  return (
    <div className="space-y-5">
      <AccountSelector accounts={accounts} value={effective} onChange={handleSelect} />

      <PromoBanner />

      {isConsolidated ? (
        consolidatedQuery.isLoading || !consolidatedQuery.data ? (
          <div className="flex min-h-[300px] items-center justify-center">
            <Spinner size={24} />
          </div>
        ) : (
          <ConsolidatedView data={consolidatedQuery.data} />
        )
      ) : metricsQuery.isLoading ? (
        <DashboardSkeleton />
      ) : metricsQuery.isError || !metricsQuery.data ? (
        <div className="border-danger-500/30 bg-danger-500/10 mx-auto max-w-xl rounded-2xl border p-6 text-center">
          <p className="text-danger-300 text-sm">Não foi possível carregar os dados da conta.</p>
        </div>
      ) : (
        <SingleAccountDashboard
          metrics={metricsQuery.data}
          equity={equityQuery.data}
          summary={summaryQuery.data}
          accountId={activeAccountId!}
          marketCode={meQuery.data?.trader?.marketCode}
          equityFailed={equityQuery.isError}
          summaryFailed={summaryQuery.isError}
        />
      )}
    </div>
  );
}

/** Esqueleto do dashboard enquanto as métricas da conta carregam. */
function DashboardSkeleton() {
  return (
    <div className="space-y-5">
      <Skeleton className="h-16 w-full" rounded="lg" />
      <div className="grid gap-5 lg:grid-cols-[1fr_340px]">
        <div className="space-y-5">
          <Skeleton className="h-24 w-full" rounded="lg" />
          <Skeleton className="h-72 w-full" rounded="lg" />
        </div>
        <Skeleton className="h-96 w-full" rounded="lg" />
      </div>
      <div className="grid items-start gap-5 lg:grid-cols-2">
        <Skeleton className="h-48 w-full" rounded="lg" />
        <Skeleton className="h-48 w-full" rounded="lg" />
      </div>
    </div>
  );
}

interface SingleProps {
  metrics: AccountMetricsResponse;
  equity?: EquityCurveResponse;
  summary?: DailySummaryResponse;
  accountId: string;
  marketCode?: 'B3' | 'FOREX';
  /** Distingue "ainda carregando" de "falhou" nos placeholders dos cards. */
  equityFailed?: boolean;
  summaryFailed?: boolean;
}

function SingleAccountDashboard({
  metrics,
  equity,
  summary,
  accountId,
  marketCode,
  equityFailed,
  summaryFailed,
}: SingleProps) {
  return (
    <div className="space-y-5">
      <AccountHeader account={metrics.account} />

      {metrics.restartOffer?.enabled && (
        <RestartOfferBanner offer={metrics.restartOffer} accountLabel={metrics.account.label} />
      )}

      {/* Resultados + gráfico (esq) · ficha da conta (dir) — estrutura FTMO */}
      <div className="grid gap-5 lg:grid-cols-[1fr_340px]">
        <div className="space-y-5">
          <CurrentResults
            balanceCents={metrics.results.balanceCents}
            equityCents={metrics.results.equityCents}
            unrealizedPnlCents={metrics.results.unrealizedPnlCents}
          />
          {/* O card NUNCA some: se o gráfico não veio, fica um bloco da mesma
              altura no lugar. Sumir deixava a coluna curta e o dashboard
              parecia incompleto. O placeholder não desenha dado nenhum —
              gráfico zerado seria pior que a ausência. */}
          {equity ? (
            <EquityCurveChart data={equity} />
          ) : (
            <div className="border-ink-800 bg-ink-900 grid h-[420px] place-items-center rounded-xl border">
              {equityFailed ? (
                <p className="text-ink-400 text-sm">Não foi possível carregar o gráfico</p>
              ) : (
                <Spinner size={20} />
              )}
            </div>
          )}
        </div>
        <AccountInfoCard account={metrics.account} marketCode={marketCode} />
      </div>

      {/* MESMO layout pra TODA conta (challenge ou funded): Disciplina (esq) ·
          Replicador (dir) e o card de objetivos full-width abaixo. O card de
          objetivos é que muda conforme a fase — Funded Pro (colchão/saque) ou
          Challenge (meta/perda máxima). O replicador é por TRADER, então
          aparece em qualquer conta de quem tem conta funded. */}
      <div className="grid items-start gap-5 lg:grid-cols-2">
        <DisciplineScore score={metrics.disciplineScore} />
        <ReplicatorCard />
      </div>
      {metrics.fundedObjectives ? (
        <FundedObjectives data={metrics.fundedObjectives} />
      ) : (
        <TradingObjectives objectives={metrics.objectives} />
      )}

      <OpenPositions />

      {metrics.violation && <ViolationBanner violation={metrics.violation} />}

      <DailyLimits results={metrics.results} />

      {/* Estatísticas (menor) · Resumo Diário (maior) */}
      <div className="grid gap-5 lg:grid-cols-[minmax(0,460px)_1fr]">
        <Statistics stats={metrics.statistics} />
        {/* Idem: o slot é preenchido mesmo sem dado. A mensagem é DIFERENTE de
            "Sem operações no período" de propósito — falha não pode se
            disfarçar de conta zerada. */}
        {summary ? (
          <DailySummary data={summary} />
        ) : (
          <Card title="Resumo Diário">
            <div className="grid place-items-center lg:h-[420px]">
              {summaryFailed ? (
                <p className="text-ink-400 py-10 text-sm">Não foi possível carregar o resumo</p>
              ) : (
                <div className="py-10">
                  <Spinner size={20} />
                </div>
              )}
            </div>
          </Card>
        )}
      </div>

      <TradingJournal accountId={accountId} />
    </div>
  );
}
